import {ref, computed, Ref, onMounted, onBeforeUnmount} from '@vue/composition-api';
import {VueWithModal, useSolutionFilters} from '../summary-filters';
import {
  getAllRecommendationsContent,
  getAmexSolution,
  loadRecommendationDetails,
  getAllSolutionFilters,
  addManualActivity,
  getTripId,
  updateSolution,
  getSessionId,
  getIsRecommendationsLoading,
  removePOIFromSolution,
  getSolutionTimezone,
  getStartEndDates,
} from '@/store/state/helpers';
import {
  UserActivityCategoryEnum,
  getNextDay,
  Timeslot,
  ConstraintType,
  IDateDescription,
  getSolutionDates,
  CandidatePoiItem,
  CroppedGoalItem,
  getBeginningOfTheDay,
} from '@mobi-components/global-solution';
import {generateEmptyActivityItem, ProblmSubmissionRoute, RecommendationContext} from '@/store/state/global.types';
import {getCandidatePoiItem, useGroup} from './group-management';
import {LunchTime} from '@/store/api/reschedule-activity';
import {getAvailableTimeSlots} from '@/store/api/download-available-timeslots';
import {formatDate, getListOfSolutionDays} from '@/store/state/utils';
import {useMealToggle} from '@/views/amex/components/explore-page/details-view/POIDetailsView/components/meal-toggle';
import {useCommonFind} from './common-find';
import {PlaceCardItem} from '@common/place-card-logic/place-card-logic';
import {fixDate} from '@/store/state/fix-solution-dates';
import {SelectOptions, SelectValueText} from '@/store/api/load-alternatives';
import {useParams} from '@/router/router';
import {AdditionalPoisModule} from '@/store/modules';
//@ts-ignore
import debounce from 'debounce';

const SORT_PREFIX = 'mobi-sort-settings';

export interface PropTypes {
  selectedDay: number;
  category: UserActivityCategoryEnum;
  isDeprecated?: boolean;
}

export interface ExploreArgs {
  root: VueWithModal;
  emit: (type: string, data?: unknown) => void;
}

export function commonExploreView(props: PropTypes, {root, emit}: ExploreArgs) {
  const params = useParams();
  const isLoadingRef = ref(false);
  const pendingActivity = ref<RecommendationContext | null>(null);
  const isTimeSlotsPending = ref(false);
  const availableTimeSlots = ref<Timeslot[]>([]);
  const contentRef = ref<HTMLDivElement | null>(null);
  const isRemovePending = ref(false);
  const slotsMap = ref<Map<string, [number, number]>>(new Map());
  const selectedGroup = ref('');

  const category = computed(() => {
    return props.category;
  });

  const selectedValue = localStorage.getItem(`${SORT_PREFIX}-${category.value}`);
  const selectedFilter = ref<SelectOptions>(selectedValue || SelectOptions.best_match);

  const isStay = computed(() => {
    return props.category === UserActivityCategoryEnum.ACCOMMODATION;
  });

  const alternatives = computed(() => {
    return getAllRecommendationsContent().filter((x) => x.context === props.category);
  });

  const solution = computed(() => {
    return getAmexSolution();
  });

  const isRecommendationsLoading = computed(() => {
    return getIsRecommendationsLoading();
  });

  const isDataLoading = computed(() => {
    return !solution.value || isLoadingRef.value || isRecommendationsLoading.value || isRemovePending.value;
  });

  const selectedExperiences = computed<Set<string>>(() => {
    const droppedPois = (solution.value?.dropped_activities || [])
      .filter((x) => x.candidate_information?.candidates?.length)
      .map((x: CroppedGoalItem) => getCandidatePoiItem(x.candidate_information))
      .map((x) => x.id);

    return new Set<string>(
      (solution.value?.activities || [])
        .filter((x) => !x.spec?.curated)
        .map((x) => {
          return [x.source?.candidate_id || x.source?.content?.id || x.id || '', x.source?.content?.source_id || ''];
        })
        .flat()
        .concat(droppedPois),
    );
  });

  const loadAdditionalExperiences = async () => {
    await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, params.value.contextName, selectedFilter.value);
    return null;
  };

  const debouncedLoadExperience = debounce(loadAdditionalExperiences, 100);

  const context = computed(() => {
    return params.value.contextName;
  });

  // watch(params, loadAdditionalExperiences);

  const activeSolutionFilters = computed<Set<string>>(() => {
    const activeTags = getAllSolutionFilters()
      .filter((x) => x.domain === props.category && x.value)
      .map((x) => x.name);
    return new Set(activeTags);
  });

  const onCloseScheduler = () => {
    pendingActivity.value = null;
  };

  const onRescheduleClick = async ({timeslot, event}: {timeslot: Timeslot | null; event: Event}) => {
    event.stopPropagation();
    const activityId = pendingActivityId.value;
    pendingActivity.value = null;
    // const dates: number[] = (solution.value?.activities || [])
    //   // @ts-ignore
    //   .map((x) => [x.original_start_time, x.original_end_time])
    //   .flat();
    // const min = Math.min(...dates) || 0;
    // const max = Math.max(...dates) || 0;
    const {start, end} = getStartEndDates();
    const date = timeslot || [Math.round(start / 1000), Math.round(end / 1000)];

    // const date = timeslot || [min, max];
    const relatedSlot = date;
    const startTime = getBeginningOfTheDay(new Date(relatedSlot[0] * 1000)).valueOf();
    const endTime = getNextDay(new Date( relatedSlot[1] * 1000)).valueOf();
  return addExperience(activityId, startTime, endTime);
  };

  const {getSelectedLunchTime, onMealChange, ...restOfMeal} = useMealToggle(category);

  const saveExperience = async (alternativeHotelId: string) => {
    const additionalPois = AdditionalPoisModule.getPois();
    const additionalPoi = additionalPois.find((x) => x.id === alternativeHotelId);
    const alt = alternatives.value.find((x) => x.id === alternativeHotelId);
    const activity = solution.value?.activities.find(
      (x) => x.id === alternativeHotelId || x.source?.candidate_id === alternativeHotelId,
    );
    const droppedActivity = solution.value?.dropped_activities.find((x) => {
      const candidate = getCandidatePoiItem(x.candidate_information);
      return candidate?.id === alternativeHotelId;
    });
    const candidateItem = droppedActivity?.candidate_information?.candidates[0];

    const selected = additionalPoi || alt || activity || candidateItem;

    // const dates: number[] = (solution.value?.activities || [])
    //   // @ts-ignore
    //   .map((x) => [x.original_start_time, x.original_end_time])
    //   .flat();
    // const min = Math.min(...dates) || 0;
    // const max = Math.max(...dates) || 0;
    const {start, end} = getStartEndDates();
    const date = [Math.round(start / 1000), Math.round(end / 1000)];

    // const date = [min, max];
    const relatedSlot = date;
    const startTime = relatedSlot[0] * 1000;
    const endTime = relatedSlot[1] * 1000;

    if (selected) {
      // @ts-ignore
      selected.start_timestamp = startTime;
      // @ts-ignore
      selected.end_timestamp = endTime;
      // @ts-ignore
      addManualActivity(selected);
    }
  };

  const addExperience = async (alternativeHotelId: string, startTime = 0, endTime = 0) => {
    const alt = alternatives.value.find((x) => x.id === alternativeHotelId);
    const activity = solution.value?.activities.find(
      (x) => x.id === alternativeHotelId || x.source?.candidate_id === alternativeHotelId,
    );
    const droppedActivity = solution.value?.dropped_activities.find((x) => {
      const candidate = getCandidatePoiItem(x.candidate_information);
      return candidate?.id === alternativeHotelId;
    });
    const candidateItem = droppedActivity?.candidate_information?.candidates[0];

    const selected = candidateItem || alt || activity?.source?.content;
    emit('item-selected', selected);
    if (contentRef.value) {
      contentRef.value.scrollTo({top: 0, behavior: 'smooth'});
    }
    isLoadingRef.value = true;

    if (selected) {
      // @ts-ignore
      selected.start_timestamp = startTime || props.selectedDay || 0;
      // @ts-ignore
      selected.end_timestamp = endTime || (props.selectedDay ? getNextDay(new Date(props.selectedDay)).valueOf() : 0);
      

      const days = solutionDates.value.filter((x) => {
        const dayStart = x.date.valueOf();
        const dayEnd = getNextDay(x.date).valueOf();
        return (
          // @ts-ignore
          (selected.start_timestamp <= dayStart && dayStart <= selected.end_timestamp) ||
          // @ts-ignore
          (selected.start_timestamp <= dayEnd && dayEnd <= selected.end_timestamp) ||
          // @ts-ignore
          (selected.start_timestamp >= dayStart && selected.start_timestamp <= dayEnd) ||
          // @ts-ignore
          (selected.end_timestamp >= dayStart && selected.end_timestamp <= dayEnd)
        );
      });

      try {
        // @ts-ignore
        await addManualActivity({...selected, timezone: getSolutionTimezone()});
        days.forEach((x) => {
          root.$emit('day-loading', {day: x.label, name: selected.name});
        });
        if (!startTime && !endTime) {
          root.$emit('day-loading', {day: 'stay', name: selected.name});
        }
        const newTripId = getTripId();
        await updateSolution(
          newTripId,
          ProblmSubmissionRoute.SOLUTION,
          [],
          getSelectedLunchTime(),
        );
      } catch {
      } finally {
        days.forEach((x) => {
          root.$emit('day-loaded', x.label);
        });
        root.$emit('day-loaded', 'stay');
      }
    }
    isLoadingRef.value = false;
    emit('item-update-completed', selected);
  };
  const onShowDetails = (alternativeHotelId: string) => {
    emit('show-details', alternativeHotelId);
    return null;
  };

  const openRescheduleOptions = async (poi: CandidatePoiItem) => {
    onMealChange(LunchTime.ANY);
    availableTimeSlots.value = [];
    isTimeSlotsPending.value = true;
    try {
      const goalItem = generateEmptyActivityItem(poi.id, poi.name, category.value, ConstraintType.NOT_APPLICABLE, 1800);
      goalItem.candidate_information = {candidates: [poi], search: null, selection: null};
      const timeslots = await getAvailableTimeSlots(goalItem, getSessionId());
      const timezone = poi.location.time_zone;
      const adjustedSlots: Timeslot[] = timeslots.map((x) => {
        const start = fixDate(x[0], timezone);
        const end = fixDate(x[1], timezone);
        return [start, end];
      });
      slotsMap.value = new Map(
        adjustedSlots.map((x, i) => {
          return [`${x[0]}-${x[1]}`, timeslots[i]];
        }),
      );
      availableTimeSlots.value = adjustedSlots;
    } catch {
      availableTimeSlots.value = [];
    }
    isTimeSlotsPending.value = false;
  };

  const onGetItemOptions = (alternativeHotelId: string, groupName = '') => {
    selectedGroup.value = groupName;
    const activity = solution.value?.activities.find(
      (x) => x.id === alternativeHotelId || x.source?.candidate_id === alternativeHotelId,
    );
    const droppedActivity = (solution.value?.dropped_activities || []).find((x) => {
      const candidate = getCandidatePoiItem(x.candidate_information);
      return candidate?.id === alternativeHotelId;
    });
    const candidateItem = droppedActivity?.candidate_information?.candidates[0];
    // @ts-ignore
    pendingActivity.value =
      getAllRecommendationsContent().find((x) => x.id === alternativeHotelId) || activity || candidateItem || null;
    if (pendingActivity.value) {
      openRescheduleOptions(pendingActivity.value);
    }
  };

  const pendingActivityId = computed(() => {
    return pendingActivity.value?.id || '';
  });

  const solutionDates = computed<IDateDescription[]>(() => {
    if (!solution.value) {
      return [];
    }
    const days = getListOfSolutionDays(solution.value);
    return getSolutionDates(days).map((x, index) => ({
      ...x,
      label: `Day ${index + 1}`,
      description: formatDate(x.date),
    }));
  });

  const onToggleMap = () => {
    emit('toggle-map');
  };

  const onRemoveUnscheduled = async (activity: PlaceCardItem) => {
    isRemovePending.value = true;
    try {
      const solutionActivity = solution.value?.activities.find((x) => x.id === activity.id || x.source?.content?.id === activity.id);
      const droppedActivity = solution.value?.dropped_activities.map((x) => {
        return x.candidate_information.candidates.find((y) => y.id === activity.id);
      })
      .filter(Boolean)
      .find(Boolean)
      const id = solutionActivity?.id || droppedActivity?.id || activity.id;
      const isPoi = !solutionActivity?.id && !droppedActivity?.id;
      await removePOIFromSolution(id, isPoi);
    } catch {
    } finally {
      isRemovePending.value = false;
    }
  };

  const selectedFilterLabel = computed(() => {
    return SelectValueText[selectedFilter.value];
  });

  const sortOptions = computed(() => {
    const base = [{value: SelectOptions.best_match, label: SelectValueText[SelectOptions.best_match]}];

    const price = [
      {value: SelectOptions.price_high, label: SelectValueText[SelectOptions.price_high]},
      {value: SelectOptions.price_low, label: SelectValueText[SelectOptions.price_low]},
    ];

    const rating = [{value: SelectOptions.rating_high, label: SelectValueText[SelectOptions.rating_high]}];

    return isStay.value ? [...base, ...price, ...rating] : [...base, ...rating];
  });

  const isSortDropdownActive = ref(false);

  const onDeactivateSortDropdown = () => {
    isSortDropdownActive.value = false;
  };

  const onFilterChange = (value: SelectOptions) => {
    onDeactivateSortDropdown();
    selectedFilter.value = value;
    loadAdditionalExperiences();
    localStorage.setItem(`${SORT_PREFIX}-${category.value}`, value);
  };

  const onActivateSortDropdown = () => {
    isSortDropdownActive.value = true;
  };

  onMounted(() => {
    document.addEventListener('click', onCloseScheduler);
    document.addEventListener('click', onDeactivateSortDropdown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', onCloseScheduler);
    document.removeEventListener('click', onDeactivateSortDropdown);
  });

  // loadAdditionalExperiences();

  return {
    isDataLoading,
    addExperience,
    selectedGroup,
    onShowDetails,
    pendingActivityId,
    pendingActivity,
    solutionDates,
    isTimeSlotsPending,
    onRescheduleClick,
    onCloseScheduler,
    availableTimeSlots,
    ...useSolutionFilters(root as VueWithModal, debouncedLoadExperience),
    ...useGroup(
      category,
      isDataLoading,
      activeSolutionFilters as Ref<Set<string>>,
      selectedExperiences as Ref<Set<string>>,
      pendingActivity,
      props.isDeprecated || false,
    ),

    ...restOfMeal,
    onMealChange,
    onGetItemOptions,
    onToggleMap,
    contentRef,
    ...useCommonFind(category, onShowDetails, debouncedLoadExperience),
    onRemoveUnscheduled,
    selectedFilterLabel,
    selectedFilter,
    onFilterChange,
    sortOptions,
    isSortDropdownActive,
    onActivateSortDropdown,
    isStay,
    context,
    saveExperience,
  };
}
